<template>
  <div
    class="py-4 container-fluid"
    :class="$store.state.isDarkMode ? 'text-slate-200' : 'text-dark'"
    :style="{ backgroundColor: $store.state.isDarkMode ? '#1E293B' : '#FAFAFA', minHeight: '100vh' }"
  >
    <div class="row">
      
      <!-- LEVÁ STRANA: Hlavní barevný box s kartami akcií -->
      <div class="col-12 col-lg-8">
        <div
          class="card border-0 p-4 rounded-4 mb-4"
          :class="$store.state.isDarkMode ? 'style-content-box-dark' : 'style-content-box'"
        >
          
          <!-- 1. ŘÁDEK: Nadpis vlevo a Vyhledávací box UPROSTŘED -->
          <div class="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-5 w-100 border-0 bg-transparent px-0">
            <h4
              class="fw-bold mb-0"
              :style="{ minWidth: '180px', color: $store.state.isDarkMode ? '#E2E8F0' : '#0F172A' }"
            >
              Přehled akcií ČR
            </h4>
            
            <!-- Vyhledávač -->
            <div class="flex-grow-1 d-flex justify-content-center mx-md-4" style="max-width: 400px; width: 100%;">
              <div class="position-relative w-100">
                <fa-icon
                  icon="fa-solid fa-magnifying-glass"
                  class="position-absolute"
                  :class="$store.state.isDarkMode ? 'text-slate-400' : 'text-muted'"
                  style="left: 14px; top: 50%; transform: translateY(-50%); font-size: 0.85rem;"
                />
                <input 
                  v-model="searchQuery"
                  type="text" 
                  class="form-control py-2 rounded-pill"
                  :class="$store.state.isDarkMode ? 'search-input-dark text-slate-200' : 'bg-white text-dark border-0 shadow-sm'"
                  :style="$store.state.isDarkMode
                    ? { paddingLeft: '38px', paddingRight: searchQuery ? '40px' : '16px', fontSize: '0.9rem', backgroundColor: '#334155', border: '1px solid #475569', color: '#E2E8F0', boxShadow: 'none' }
                    : { paddingLeft: '38px', paddingRight: searchQuery ? '40px' : '16px', fontSize: '0.9rem' }"
                  placeholder="Hledat podle názvu nebo ISIN..."
                />
                <button 
                  v-if="searchQuery"
                  @click="searchQuery = ''"
                  class="btn p-0 border-0 position-absolute search-clear-btn"
                  :class="$store.state.isDarkMode ? 'text-slate-400' : 'text-muted'"
                  style="right: 12px; top: 50%; transform: translateY(-50%); line-height: 1;"
                  type="button"
                  aria-label="Smazat filtr"
                >
                  <fa-icon icon="fa-solid fa-times" />
                </button>
              </div>
            </div>

            <!-- Celkový počet titulů -->
            <span
              class="badge px-3 py-2 rounded-pill font-weight-normal"
              :class="$store.state.isDarkMode ? 'badge-count-dark' : 'bg-white text-muted border shadow-sm'"
              :style="$store.state.isDarkMode
                ? { fontSize: '0.8rem', backgroundColor: '#334155', color: '#E2E8F0', border: '1px solid #475569', boxShadow: 'none' }
                : { fontSize: '0.8rem', borderColor: '#E2E8F0' }"
            >
              Celkem: {{ ISINList.length }} titulů
            </span>
          </div>

          <!-- 2. ŘÁDEK: Komponenta karet roztažená na 100 % šířky -->
          <div class="w-100">
            <shares-table :rows="ISINList" :searchQuery="searchQuery" />
          </div>

        </div>
      </div>

      <!-- PRAVÁ STRANA: Herní panely + Slečna INGA s dynamickou bublinou -->
      <div class="col-12 col-lg-4 mt-4 mt-lg-0">
        
        <!-- 🔌 INCLUDNUTÁ ZNOVUPOUŽITELNÁ KOMPONENTA HER (Zobrazí aktuální hry z jednoho místa) -->
        <ActiveGamesSidebar />

        <!-- DLAŽDICE S INGOU: Karta pro slečnu INGU s komiksovou bublinou (Zůstává zde pro dynamické hlášky z trhu) -->
        <div
          class="border-0 rounded-4 text-center img-inga-card position-relative"
          :class="$store.state.isDarkMode ? 'p-0' : 'p-3'"
          :style="$store.state.isDarkMode
            ? 'background: transparent !important; border: none !important; box-shadow: none !important; border-radius: 0.65rem !important;'
            : 'background: #ffffff !important; border: none !important; box-shadow: none !important; border-radius: 0.65rem !important;'"
        >
          <div v-if="ingaText" class="inga-speech-bubble" :class="{ 'inga-speech-bubble-dark': $store.state.isDarkMode }">
            {{ ingaText }}
          </div>
          <IngaPortrait :src="imgRoot + 'inga.jpg'" />
        </div>

      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, inject } from "vue";
import { useStore } from "vuex";
import dataService from "@/services/dataService.js";
import SharesTable from "./components/custom/tables/SharesTable.vue";
// Importujeme sdílenou komponentu her (zkontroluj relativní cestu podle struktury složek)
import ActiveGamesSidebar from "./components/custom/ActiveGamesSidebar.vue";
import IngaPortrait from "./components/custom/IngaPortrait.vue";

const swal = inject('$swal');
const store = useStore();
const ISINList = ref([]);
const searchQuery = ref("");
const ingaText = ref("Čiluju a koukám, co se děje na trhu... 🦥☕");

const imgRoot = computed(() => {
  return process.env.VUE_APP_IMG_ROOT;
});

// Funkce generování hlášek Ingy podle stavu akcií zůstává v Shares
const generateIngaComment = (stocks) => {
  if (!stocks || stocks.length === 0) {
    ingaText.value = "Přemýšlím, kam se ty trhy dneska vrtnou... 🧠💭";
    return;
  }

  let rocket = null;
  let maxGrowth = -999;
  let dumper = null;
  let maxDrop = 999;
  let slacker = null;
  let minChange = 999;

  stocks.forEach(share => {
    if (share.PctDf) {
      const rawPct = parseFloat(share.PctDf.replace('+', '').replace(',', '.'));
      if (!isNaN(rawPct)) {
        if (rawPct > maxGrowth) {
          maxGrowth = rawPct;
          rocket = share;
        }
        if (rawPct < maxDrop) {
          maxDrop = rawPct;
          dumper = share;
        }
        const absPct = Math.abs(rawPct);
        if (absPct < minChange) {
          minChange = absPct;
          slacker = share;
        }
      }
    }
  });

  const pool = [];
  if (rocket && maxGrowth > 0) {
    if (maxGrowth >= 4.5) pool.push(`Ty bláho! Sleduješ ${rocket.Nm}? Letí strmě nahoru o šílených +${rocket.PctDf}%! 🚀🔥`);
    else pool.push(`Dneska se na burze nejvíc daří akcii ${rocket.Nm}. Připisuje si pěkných +${rocket.PctDf}%. 📈`);
  }
  if (dumper && maxDrop < 0) {
    if (maxDrop <= -4.5) pool.push(`Uf, na trhu je divočina! Akcie ${dumper.Nm} zažívá brutální volný pád a odepisuje ${dumper.PctDf}%. 📉😱`);
    else pool.push(`Slečna INGA hlásí, že ${dumper.Nm} dneska nemá svůj den. Klesá o ${dumper.PctDf}%. 🛍️`);
  }
  if (slacker) pool.push(`Největším lenochem dne je jednoznačně ${slacker.Nm}. Změna o pouhých ${slacker.PctDf}%... 💤🦥`);

  if (pool.length === 0) {
    ingaText.value = "Přemýšlím, co dneska provedu s penězi. Na trhu je hroznej klid... ☕";
    return;
  }

  const randomIndex = Math.floor(Math.random() * pool.length);
  ingaText.value = pool[randomIndex];
};

const fetchISINListData = async () => {
  try {
    const response = await dataService.GetISINList();
    if (response.Result) {
      ISINList.value = response.Data;
      generateIngaComment(response.Data);
    }
  } catch (error) {
    console.error(error);
  }
};

fetchISINListData();
</script>

<style scoped>
.text-slate-200 { color: #E2E8F0 !important; }
.text-slate-400 { color: #94A3B8 !important; }

.style-content-box {
  background-color: #FFF9F2 !important;
  border: 1px solid #FBEFDF !important;
  border-radius: 1rem !important;
}

.style-content-box-dark {
  background-color: #0F172A !important;
  border: 1px solid #334155 !important;
  border-radius: 1rem !important;
}

.search-input-dark {
  background-color: #334155 !important;
  background: #334155 !important;
  border: 1px solid #475569 !important;
  color: #E2E8F0 !important;
  box-shadow: none !important;
}

.search-input-dark::placeholder {
  color: #94A3B8 !important;
}

.search-input-dark:focus {
  background-color: #334155 !important;
  background: #334155 !important;
  color: #E2E8F0 !important;
  border-color: #FF8A4C !important;
  box-shadow: 0 0 0 0.15rem rgba(255, 138, 76, 0.25) !important;
}

.search-clear-btn {
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.search-clear-btn:hover {
  color: #FF8A4C !important;
}

.badge-count-dark {
  background-color: #334155 !important;
  background: #334155 !important;
  color: #E2E8F0 !important;
  border: 1px solid #475569 !important;
  box-shadow: none !important;
}

.img-inga-card {
  margin-top: 3.5rem !important;
  border: none !important;
  border-radius: 0.65rem !important;
  box-shadow: none !important;
  overflow: visible;
}

.img-inga-card:hover {
  transform: none;
  box-shadow: none !important;
}

.inga-speech-bubble {
  position: absolute;
  top: -65px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #ffffff;
  border: 2px solid #FF7A00;
  color: #1E293B;
  font-weight: 700;
  font-size: 0.82rem;
  padding: 10px 14px;
  border-radius: 16px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.06);
  z-index: 10;
  width: 92%;
  max-width: 280px;
}

@media (min-width: 768px) {
  .inga-speech-bubble {
    top: -60px;
    padding: 12px 18px;
    font-size: 0.88rem;
    max-width: 320px;
  }
}

.inga-speech-bubble-dark {
  background-color: #1E293B;
  color: #E2E8F0;
  border-color: #FF7A00;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.25);
}

.inga-speech-bubble::after, .inga-speech-bubble::before {
  content: ''; position: absolute; bottom: -10px; left: 50%; transform: translateX(-50%); border-style: solid; display: block; width: 0;
}
.inga-speech-bubble::after { border-width: 10px 10px 0; border-color: #ffffff transparent transparent; z-index: 11; }
.inga-speech-bubble::before { bottom: -13px; border-width: 11px 11px 0; border-color: #FF7A00 transparent transparent; z-index: 9; }
.inga-speech-bubble-dark::after { border-color: #1E293B transparent transparent; }
</style>

